<template>
  <div class="modal-content">
    <div class="modal-header">
      <h4 class="modal-title">{{ _('Add to user interface') }}</h4>
    </div>
    <div class="modal-body">
      <p v-if="loading" class="text-muted"><i class="fa fa-fw fa-cog fa-spin"></i> {{ _('Loading...') }}</p>
      <p v-else-if="loadFailed" class="text-danger">{{ _('The user interfaces could not be loaded.') }}</p>
      <template v-else>
        <p class="help-block">{{ _('This adds a new menu section with the components already arranged.') }}</p>
        <div class="form-group form-group-tight">
          <label>{{ _('User interface') }}</label>
          <div v-if="!showClientPicker" class="checkbox current-ui">
            <label>
              <input type="checkbox" checked disabled />
              {{ selectedClientName }}
              <small v-if="selectedClient && selectedClient.current" class="text-muted"> · {{ _('This user interface') }}</small>
            </label>
          </div>
          <div v-else class="client-picker">
            <div v-for="client in destinations" :key="client.id" class="radio">
              <label>
                <input type="radio" :value="client.id" v-model.number="selectedClientId" />
                {{ client.name || ('#' + client.id) }}
                <small v-if="client.current" class="text-muted"> · {{ _('This user interface') }}</small>
                <small v-else-if="client.istemplate" class="text-muted"> · {{ _('Template') }}</small>
              </label>
            </div>
          </div>
          <button v-if="destinations.length > 1" type="button" class="extra-toggle" v-on:click="showClientPicker = !showClientPicker">
            {{ showClientPicker ? _('Cancel') : _('Change UI') }}
          </button>
        </div>
        <div class="form-group">
          <label for="uiFeaturePlaceName">{{ _('Section name') }}</label>
          <input id="uiFeaturePlaceName" type="text" class="form-control" v-model="sectionName" :placeholder="recipeTitle" />
        </div>
        <div class="form-group">
          <label>{{ _('Icon') }}</label><br />
          <button type="button" class="btn btn-default" v-on:click="selectIconDialog">{{ _('Select icon') }}</button>
          <i v-if="icon" :class="'fa fa-fw fa-2x fa-' + icon"></i>
          <button v-if="icon && icon !== defaultIcon" type="button" class="btn btn-danger" v-on:click="icon = defaultIcon">{{ _('Remove icon') }}</button>
        </div>
      </template>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-default" v-on:click="hideModal"><i class="fa fa-fw fa-close"></i> {{ _('Cancel') }}</button>
      <button type="button" class="btn btn-success" :disabled="!canSubmit" v-on:click="submit">
        <i class="fa fa-fw" :class="saving ? 'fa-cog fa-spin' : 'fa-plus'"></i>
        {{ saving ? _('Saving...') : _('Add to user interface') }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import errors from '~/lib/errors'
import modals from '~/lib/modals'
import versions from '~/lib/versions'

export default {
  mixins: [errors, modals, versions],
  props: {
    recipeId: { type: String, required: true },
    recipeTitle: { type: String, default: '' },
    recipeIcon: { type: String, default: '' },
    pickOtherClient: { type: Boolean, default: false }
  },
  data () {
    return {
      loading: true,
      loadFailed: false,
      saving: false,
      destinations: [],
      sectionName: '',
      icon: '',
      selectedClientId: null,
      showClientPicker: false
    }
  },
  computed: {
    ...mapGetters(['shopId', 'baseDomain', 'apiKey', 'clientIdNumber']),
    defaultIcon () {
      return this.recipeIcon || 'puzzle-piece'
    },
    selectedClient () {
      return this.destinations.find(client => client.id === this.selectedClientId)
        || this.destinations.find(client => client.current)
        || this.destinations[0]
        || null
    },
    selectedClientName () {
      return this.selectedClient ? (this.selectedClient.name || ('#' + this.selectedClient.id)) : ''
    },
    canSubmit () {
      return !this.loading && !this.loadFailed && !this.saving && !!this.selectedClient
    }
  },
  watch: {
    selectedClientId (id, previous) {
      if (previous != null) {
        this.showClientPicker = false
      }
    }
  },
  methods: {
    hideModal () {
      this.$emit('close')
    },
    selectIconDialog () {
      this.showDialog({
        title: this._('Select icon'),
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            handler: (data) => {
              this.icon = data.icon || this.defaultIcon
            }
          }
        ],
        fields: [
          {
            title: this._('Icon'),
            type: 'icon',
            name: 'icon',
            defaultValue: this.icon
          }
        ]
      })
    },
    async loadDestinations () {
      this.loading = true
      this.loadFailed = false
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/uifeatures/' + this.recipeId + '.json?destinations=1&client_id=' + (this.clientIdNumber || 0) + '&apikey=' + this.apiKey + this.versionStr)
        this.destinations = Array.isArray(response.data.destinations) ? response.data.destinations : []
        this.sectionName = this.recipeTitle
        this.icon = this.defaultIcon
        const current = this.destinations.find(client => client.current)
          || this.destinations.find(client => client.id === this.clientIdNumber)
        const other = this.destinations.find(client => !client.current && client.id !== this.clientIdNumber)
        if (this.pickOtherClient && other) {
          this.selectedClientId = other.id
          this.showClientPicker = true
        } else {
          this.selectedClientId = current ? current.id : (this.destinations[0] ? this.destinations[0].id : null)
          this.showClientPicker = false
        }
      } catch (e) {
        this.loadFailed = true
        this.showError(e)
      }
      this.loading = false
    },
    async submit () {
      if (!this.canSubmit) {
        return
      }
      this.saving = true
      try {
        const response = await axios.patch(this.baseDomain + this.shopId + '/uifeatures/' + this.recipeId + '.json?apikey=' + this.apiKey + this.versionStr, {
          id: this.recipeId,
          action: 'place',
          client_ids: [this.selectedClient.id],
          section_name: this.sectionName || this.recipeTitle,
          icon: this.icon || this.defaultIcon
        })
        this.$emit('placed', response.data.placed)
      } catch (e) {
        this.saving = false
        return this.showError(e)
      }
      this.saving = false
    }
  },
  mounted () {
    this.loadDestinations()
  }
}
</script>

<style scoped>
.form-group-tight {
  margin-bottom: 8px;
}
.current-ui {
  margin-top: 0;
  margin-bottom: 0;
}
.current-ui label {
  color: inherit;
  cursor: default;
}
.current-ui input[disabled] {
  opacity: 1;
  cursor: default;
}
.extra-toggle {
  display: inline-flex;
  align-items: center;
  padding: 0;
  margin: 4px 0 0;
  border: 0;
  background: none;
  color: #337ab7;
  font-size: inherit;
  line-height: 1.4;
  cursor: pointer;
}
.extra-toggle:hover,
.extra-toggle:focus {
  text-decoration: underline;
}
.client-picker {
  max-height: 180px;
  overflow-y: auto;
}
.client-picker .radio {
  margin-top: 4px;
  margin-bottom: 4px;
}
</style>
